import React from 'react';
import './waterBookContent.scss';
import {POST_XHTTP} from "../../../services/xhttps";
import {useFormik} from "formik";
import * as Yup from "yup";
import Input from "../../../components/ui/form/Input";
import {InputType} from "../../../types/UITypes";
import Button from "../../../components/ui/button/Button";
import Checkbox from "../../../components/ui/form/Checkbox";
import {graphql, Link, useStaticQuery} from "gatsby";
import {GatsbyImage} from "gatsby-plugin-image";
import routes from "../../../services/routes";

interface WhiteBookFormValues {
    firstName: string;
    lastName: string;
    email: string;
    subscribedToNewsletter: boolean;
    subscribedToPartnerNewsletter: boolean;
}

const WaterBookContent = () => {
    const handleSubmit = (values: WhiteBookFormValues) => {
        POST_XHTTP('external/livre-blanc/water', { ...values, crossOrigin: false })
            .then(() => {
                alert(`Le livre blanc t'a été envoyé par email ! Si tu ne le trouves pas, pense à vérifier ta boîte de spam ;)`);
                formik?.resetForm();
            })
            .catch(() => alert('Oops, quelque chose s\'est mal passé...'));
    };

    const formik = useFormik({
        initialValues: {
            email: '',
            firstName: '',
            lastName: '',
            subscribedToNewsletter: false,
            subscribedToPartnerNewsletter: false,
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Adresse email non valide').required('Renseigne une adresse email valide'),
            firstName: Yup.string().required('Renseigne ton prénom'),
            lastName: Yup.string().required('Renseigne ton nom'),
            subscribedToNewsletter: Yup.boolean()
                .oneOf([true], "Champ obligatoire")
                .required("Champ obligatoire"),
            subscribedToPartnerNewsletter: Yup.boolean(),
        }),
        onSubmit: values => handleSubmit(values),
    });

    const data = useStaticQuery(graphql`
    query WaterWhiteBookImagesQuery {
      claire: file(name: {eq: "claire_hartmann"}) {
        id
        childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, width: 350, transformOptions: {fit:COVER})
        }
      }
      energic: file(name: {eq: "logo_energic_blue"}) {
        id
        childImageSharp {
            gatsbyImageData(layout: CONSTRAINED, width: 150, transformOptions: {fit:COVER})
        }
      }
      illus: file(name: {eq: "water_white_book"}) {
        id
        childImageSharp {
            gatsbyImageData(layout: CONSTRAINED)
        }
      }
    }
  `);

    return (
        <div className='whiteBookContainer'>
            <section className='whiteBookContent'>
                <div className='whiteBookPresentation'>
                    <h1>Agir pour l’eau et l’océan, une nécessité collective</h1>
                    <p>
                        L’eau douce est une ressource limitée, tandis que l’océan subit une pression croissante due aux activités humaines. Avec ce livre blanc préfacé par
                        &nbsp;<a
                            href="https://www.surfrider.fr/?gad_source=1&gclid=CjwKCAjw7pO_BhAlEiwA4pMQvJtV3ToOu_J9iATnfdUj7eeVk2ZaWqTEJ_nXZ7pvKQKd2IOuKXg3eRoCqv4QAvD_BwE"
                            target="_blank"
                        >Surfrider Europe</a>
                        , plongez à la découverte des enjeux de ces ressources vitales et découvrez comment les préserver en tant qu’entreprise et citoyen !
                    </p>


                    <form
                        className='whiteBookForm'
                        onSubmit={formik.handleSubmit}
                    >
                        <div className='fieldsRow'>
                            <Input
                                value={formik.values.lastName}
                                type={InputType.Text}
                                onChange={formik.handleChange}
                                name='lastName'
                                id='lastNameWB'
                                errors={formik.errors.lastName}
                                touched={formik.touched.lastName}
                                label="Nom*"
                            />
                            <Input
                                value={formik.values.firstName}
                                type={InputType.Text}
                                onChange={formik.handleChange}
                                name='firstName'
                                id='firstNameWB'
                                errors={formik.errors.firstName}
                                touched={formik.touched.firstName}
                                label="Prénom*"
                            />
                        </div>
                        <Input
                            value={formik.values.email}
                            type={InputType.Text}
                            onChange={formik.handleChange}
                            name='email'
                            id='emailWB'
                            errors={formik.errors.email}
                            touched={formik.touched.email}
                            label="Adresse email*"
                        />
                        <Checkbox
                            label={(
                                <>
                                    J’autorise Energic à utiliser les données personnelles saisies dans ce formulaire pour m’envoyer des informations commerciales comme décrit dans la&nbsp;
                                    <Link to={routes.footer.websitePolicy} className='policyLink'>Politique de confidentialité</Link>*
                                </>
                            )}
                            name="subscribedToNewsletter"
                            id="subscribedToNewsletterCheckbox"
                            errors={formik.errors.subscribedToNewsletter}
                            checked={formik.values.subscribedToNewsletter}
                            onClick={(e) =>
                                formik.setFieldValue("subscribedToNewsletter", e?.target?.checked)
                            }
                        />
                        <Checkbox
                            label="Je souhaite également recevoir la newsletter mensuelle de Surfrider Europe"
                            name="subscribedToPartnerNewsletter"
                            id="subscribedToPartnerNewsletterCheckbox"
                            errors={formik.errors.subscribedToPartnerNewsletter}
                            checked={formik.values.subscribedToPartnerNewsletter}
                            onClick={(e) =>
                                formik.setFieldValue("subscribedToPartnerNewsletter", e?.target?.checked)
                            }
                        />
                        <Button
                            label="Recevoir le livre blanc"
                            className='whiteBookFormButton'
                            type='submit'
                            role='button'
                        />
                    </form>
                </div>
                <div className='whiteBookPortraits'>
                    <a
                        className='portrait'
                        title="Profil LinkedIn de Claire Hartmann"
                        href="https://www.linkedin.com/in/claire-hartmann-1b28ba177"
                        target='_blank'
                    >
                        <GatsbyImage
                            alt="Illustration : Claire Hartmann"
                            image={data?.claire?.childImageSharp?.gatsbyImageData}
                            objectFit="contain"
                            className="ClaireImage"
                        />
                        <p>
                            Claire Hartmann<br/>
                            Responsable Communication Energic
                        </p>
                    </a>
                </div>
                <div className='whiteBookImage'>
                    <GatsbyImage
                        alt="Illustration : Livre Blanc"
                        image={data?.illus?.childImageSharp?.gatsbyImageData}
                        objectFit="contain"
                        className="WhiteBookImage"
                        imgStyle={{
                            maxWidth: 500,
                            alignSelf: 'center'
                        }}
                    />
                    <div className='whiteBookLogos'>
                        <p>Proposé&nbsp;par&nbsp;</p>
                        <GatsbyImage
                            alt="Illustration : Logo Energic"
                            image={data?.energic?.childImageSharp?.gatsbyImageData}
                            objectFit="contain"
                            className="WhiteBookLogo"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
};

export default WaterBookContent;